import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";

const TRAIN_IMG =
  "https://images.unsplash.com/photo-1582217900003-2b19c0e3a7d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjB0cmFpbiUyMHJhaWx3YXklMjBqb3VybmV5JTIwdHJhY2t8ZW58MHx8fHwxNzkzMTgyMzg1fDA&ixlib=rb-4.1.0&q=85";

const cardVariants = {
  hidden: { opacity: 0, y: 34 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Stations({ stations, onOrder }) {
  return (
    <section id="stations" className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32" data-testid="stations-section">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mb-14 max-w-2xl"
      >
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.24em] text-amber-700">Delivery network</p>
        <h2 className="font-display text-4xl font-bold tracking-tight text-emerald-950 sm:text-5xl" data-testid="stations-heading">
          We meet your train at <span className="italic text-amber-600">these stations</span>
        </h2>
        <p className="mt-4 text-base text-emerald-950/70 sm:text-lg">
          Order at least an hour before arrival. Our delivery partner boards the platform and hands
          the thali straight to you.
        </p>
      </motion.div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="stations-grid">
        {stations.map((s, i) => (
          <motion.article
            key={s._id}
            custom={i}
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className={`group relative flex flex-col justify-between overflow-hidden rounded-3xl border border-amber-900/10 bg-white/70 p-7 shadow-sm backdrop-blur transition-shadow hover:shadow-xl hover:shadow-amber-900/10 ${
              i === 0 ? "sm:col-span-2 lg:row-span-2" : ""
            }`}
            data-testid={`station-card-${s.code}`}
          >
            {i === 0 && (
              <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                <img src={TRAIN_IMG} alt="" className="h-full w-full object-cover opacity-[0.10]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FFFBEB] via-[#FFFBEB]/60 to-transparent" />
              </div>
            )}
            <div className="relative">
              <div className="mb-5 flex items-center justify-between">
                <span className="rounded-full bg-emerald-900 px-3 py-1 text-xs font-bold tracking-widest text-amber-100" data-testid={`station-code-${s.code}`}>
                  {s.code}
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
                    s.delivery_available ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"
                  }`}
                  data-testid={`station-availability-${s.code}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${s.delivery_available ? "bg-emerald-600" : "bg-red-500"}`} />
                  {s.delivery_available ? "Delivering" : "Paused"}
                </span>
              </div>
              <h3 className={`flex items-start gap-2 font-display font-bold text-emerald-950 ${i === 0 ? "text-3xl sm:text-4xl" : "text-2xl"}`}>
                <MapPin className="mt-1.5 h-5 w-5 shrink-0 text-amber-600" />
                {s.name}
              </h3>
              <p className="mt-3 text-sm font-semibold text-emerald-950/70" data-testid={`station-charge-${s.code}`}>
                Delivery ₹{s.delivery_charge} · order {s.cutoff_minutes} min before arrival
              </p>
            </div>
            <button
              onClick={() => onOrder(s._id)}
              disabled={!s.delivery_available}
              data-testid={`station-order-button-${s.code}`}
              className="relative mt-7 inline-flex w-fit items-center gap-2 rounded-full bg-amber-600 px-6 py-2.5 text-sm font-bold text-amber-50 transition-all enabled:hover:gap-3 enabled:hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Order Now <ArrowRight className="h-4 w-4" />
            </button>
          </motion.article>
        ))}
      </div>
    </section>
  );
}
